import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  AuditApi,
  AuditRunResult,
  CreateTaskItem,
  CreateTasksResponse,
  DispatchDecision,
  TeamManager,
} from './types';

// Backend errors (e.g. the real ClickUp calls in createTasks
// failing against sentinel/missing credentials) come back as a JSON error
// body with a non-2xx status, not a value matching the success type. Without
// this check, res.json() happily resolves with that error body and callers
// end up treating it as the real payload (e.g. reading .kind or .results off
// it), crashing the page instead of surfacing a catchable error.
async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      // response body wasn't JSON; fall back to the status-based message
    }
    throw new Error(message);
  }
  return res.json();
}

export class AuditApiClient implements AuditApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('service-catalog-audit');
  }

  async canDispatch(): Promise<DispatchDecision> {
    const res = await this.fetchApi.fetch(
      `${await this.baseUrl()}/can-dispatch`,
    );
    return readJsonOrThrow<DispatchDecision>(res);
  }

  async getLatestRun(): Promise<AuditRunResult> {
    const res = await this.fetchApi.fetch(`${await this.baseUrl()}/latest`);
    return readJsonOrThrow<AuditRunResult>(res);
  }

  async runAudit(): Promise<AuditRunResult> {
    const res = await this.fetchApi.fetch(`${await this.baseUrl()}/run`, {
      method: 'POST',
    });
    return readJsonOrThrow<AuditRunResult>(res);
  }

  async getManager(team: string): Promise<TeamManager> {
    const url = new URL(`${await this.baseUrl()}/manager`);
    url.searchParams.set('team', team);
    const res = await this.fetchApi.fetch(url.toString());
    return readJsonOrThrow<TeamManager>(res);
  }

  async createTasks(items: CreateTaskItem[]): Promise<CreateTasksResponse> {
    const res = await this.fetchApi.fetch(
      `${await this.baseUrl()}/create-tasks`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      },
    );
    return readJsonOrThrow<CreateTasksResponse>(res);
  }
}
